/**
 * The October roadmap's board pieces (docs/roadmap-2026-10.md, docs/weekly.md), each behind its own
 * flag and drawn the same on the Expo client (client/components/DoneExtras.tsx):
 *
 * - useWeeklyScroll (`weeklyUI`): the newest weekly scroll, which src/components/WeeklyScroll.tsx draws
 *   in its launch sheet and on the open board's notice board.
 * - DoneViewToggle and DoneGallery (`galleryUI`): List or Gallery, the after photos in a grid.
 * - EmptyBoard (`peonMoodsUI`): the peon asleep on his gold when nothing at all is open.
 * - CrownViewer and useLongPress (no flag): the Peon of the Month's look large on a long press.
 *
 * The rules live in shared/weekly.ts; this file only draws them.
 */
import "./done-extras.css";
import { createPortal } from "preact/compat";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { href } from "../router";
import { useEscape } from "../escape";
import { useExit } from "./useExit";
import { useScreenCue } from "./ScreenCue";
import { PeonLayers } from "./PeonLayers";
import { PixelIcon } from "./PixelArt";
import { SmallPeon } from "./SmallPeon";
import type { BoardSummary, Task } from "../../shared/types";
import type { LayeredLook } from "../peon-art.ts";
import { DEFAULT_SHIRT, isShirt } from "../../shared/shirts.ts";
import {
  CROWN_LONG_PRESS_MS,
  DONE_VIEW_KEY,
  DONE_VIEW_OPTIONS,
  EMPTY_BOARD_LINE,
  GALLERY_EMPTY,
  doerLook,
  galleryLabel,
  parseDoneView,
  type DoneView,
  type WeeklyScroll,
} from "../../shared/weekly.ts";
import { radioKeys } from "../radio-group";

// ---------- The weekly scroll ----------

/** The newest scroll this page has read: the card paints from it on the next visit, then refreshes. */
let remembered: WeeklyScroll | null = null;

/** The household's newest scroll while `enabled`, read on each showing of the Done board; null before the first Sunday. */
export function useWeeklyScroll(enabled: boolean): WeeklyScroll | null {
  const [scroll, setScroll] = useState<WeeklyScroll | null>(enabled ? remembered : null);
  useEffect(() => {
    if (!enabled) {
      setScroll(null);
      return;
    }
    let live = true;
    api.weekly
      .latest()
      .then((r) => {
        remembered = r.scroll;
        if (live) setScroll(r.scroll);
      })
      .catch(() => {
        /* offline: keep what was shown */
      });
    return () => {
      live = false;
    };
  }, [enabled]);
  return scroll;
}

// ---------- The Done gallery ----------

function readDoneView(): DoneView {
  try {
    return parseDoneView(localStorage.getItem(DONE_VIEW_KEY));
  } catch {
    return "list";
  }
}

/** The device's List or Gallery, remembered under op.doneView. */
export function useDoneView(): [DoneView, (v: DoneView) => void] {
  const [view, setView] = useState<DoneView>(readDoneView);
  const choose = useCallback((v: DoneView) => {
    setView(v);
    try {
      localStorage.setItem(DONE_VIEW_KEY, v);
    } catch {
      /* private mode: the choice lasts the visit */
    }
  }, []);
  return [view, choose];
}

/** List or Gallery, in the segmented strip every other choice of view uses. */
export function DoneViewToggle({ view, onChange }: { view: DoneView; onChange: (v: DoneView) => void }) {
  return (
    <div class="segmented done-view" role="radiogroup" aria-label="Show done tasks as" onKeyDown={radioKeys}>
      {DONE_VIEW_OPTIONS.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={view === o.value} class={view === o.value ? "active" : ""} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The peon who did it, small, on a bronze-edged plate in the tile's bottom-left corner (owner,
 * 2026-10-05: "In gallery, a small version of the peon who did it should be in the corner"): the
 * small peon (SmallPeon: the mascot in the doer's shirt with their hair, beard and accessories,
 * shared/weekly.ts GALLERY_PEON). Hidden from a screen reader: the tile's own name says who.
 */
function GalleryPeon({ look }: { look: GalleryLook }) {
  const shirt = isShirt(look.shirt) ? look.shirt : DEFAULT_SHIRT;
  return (
    <span class="gallery-peon" aria-hidden="true" data-shirt={shirt}>
      <SmallPeon look={look} />
    </span>
  );
}

type GalleryLook = NonNullable<BoardSummary["looks"]>[string];

/**
 * The after photos of what is loaded, newest first, three across in square bronze-edged tiles; a tile
 * opens its task. Each wears the peon who did it in its corner, from the summary's `looks`.
 */
export function DoneGallery({ tasks, looks }: { tasks: Task[]; looks?: BoardSummary["looks"] }) {
  if (tasks.length === 0) return <div class="empty">{GALLERY_EMPTY}</div>;
  return (
    <ul class="done-gallery">
      {tasks.map((t) => {
        const look = doerLook(looks, t.done_by);
        return (
          <li key={t.id}>
            <a class="gallery-tile" href={href({ name: "task", id: t.id })} aria-label={galleryLabel(t)}>
              <img src={api.photos.thumbUrl(t.done_photo_id!)} alt="" loading="lazy" />
              {look && <GalleryPeon look={look} />}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

// ---------- The empty board ----------

/** The peon asleep on a heap of gold (scripts/make-empty-board.mjs); the z's step in hard frames unless motion is reduced. */
export function EmptyBoard() {
  return (
    <div class="empty-board">
      <div class="empty-board-art" role="img" aria-label="The peon asleep on a pile of gold" />
      <p class="empty-board-line">{EMPTY_BOARD_LINE}</p>
    </div>
  );
}

// ---------- The crown up close ----------

/**
 * A long press (CROWN_LONG_PRESS_MS) or a right click calls `onLong`; the click a long press ends in
 * is swallowed, so a short press still follows the link as before. A finger that moves is a scroll.
 */
export function useLongPress(onLong: (() => void) | null) {
  const timer = useRef(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const latest = useRef(onLong);
  latest.current = onLong;
  const cancel = () => {
    window.clearTimeout(timer.current);
    timer.current = 0;
    start.current = null;
  };
  useEffect(() => cancel, []);
  return {
    onPointerDown: (e: PointerEvent) => {
      fired.current = false;
      if (!latest.current || e.button !== 0) return;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        timer.current = 0;
        fired.current = true;
        latest.current?.();
      }, CROWN_LONG_PRESS_MS);
    },
    onPointerMove: (e: PointerEvent) => {
      const s = start.current;
      if (s && Math.abs(e.clientX - s.x) + Math.abs(e.clientY - s.y) > 10) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu: (e: Event) => {
      if (!latest.current) return;
      e.preventDefault();
      cancel();
      if (!fired.current) latest.current();
      fired.current = true;
    },
    onClickCapture: (e: Event) => {
      if (!fired.current) return;
      fired.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };
}

/**
 * The winner's full look, large: the crowned portrait the card wears (with the hair, beard and
 * accessories over the original art), crisp at five art pixels a pixel, in the gold frame, the name
 * and the month line under it. The viewer's black and its stepped fade; a tap anywhere, the X or
 * Escape closes it.
 */
export function CrownViewer({ portrait, look, name, line, onClose }: { portrait: string; look: LayeredLook | null; name: string; line: string; onClose: () => void }) {
  const exit = useExit(onClose);
  useScreenCue(exit.closing);
  useEscape(!exit.closing, () => exit.close());
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    return () => {
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  return createPortal(
    <div class={`lightbox crown-viewer${exit.cls}`} ref={exit.ref} role="dialog" aria-modal="true" aria-label={`${name}, Peon of the Month`} onClick={() => exit.close()}>
      <button ref={closeRef} class="lightbox-close" type="button" aria-label="Close" onClick={() => exit.close()}>
        <PixelIcon name="close" />
      </button>
      <div class="crown-viewer-body">
        <div class="potm crown-viewer-frame">
          <span class="peon-stack crown-viewer-stack">
            <img class="crown-viewer-portrait" src={portrait} alt="" />
            {look && <PeonLayers look={look} crowned />}
          </span>
        </div>
        <div class="crown-viewer-name">{name}</div>
        <div class="crown-viewer-line">{line}</div>
      </div>
    </div>,
    document.body,
  );
}
